import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from 'js/src/agent/skills/types';

export const parallaxDepthDriftSkill: Skill = {
    name: SKILL_NAMES.parallaxDepthDrift,
    kind: 'codeo',
    description:
        'Drift layered background geometry at depth-scaled speeds over the whole scene so the ' +
        'world has dimension — for compositions whose background feels like a flat colored card.',
    instructions: `# Parallax Depth Drift

Two or three background layers drift slowly in the same direction at different speeds — far layer slowest, near layer fastest — and the differential reads as depth. It runs the full length of the scene underneath everything, turning a flat backdrop into a world.

## Markup

Each layer is a plain \`<div>\` holding the world's decorative shapes (soft discs, grain fields, brand motifs), oversized so its edges never show while drifting.

\`\`\`html
<div id="bg-far" style="position: absolute; inset: -8%; z-index: 0; background: var(--l5-color-2, #d7d7e2);">
  <!-- large soft shapes, lowest contrast -->
</div>
<div id="bg-near" style="position: absolute; inset: -8%; z-index: 0; pointer-events: none;">
  <!-- smaller, slightly higher-contrast shapes -->
</div>
\`\`\`

## Timeline code

One drift tween per layer, spanning the whole scene, linear ease, speeds scaled by depth:

\`\`\`js
const tl = gsap.timeline({ paused: true });
const DUR = 12; // the scene's full on-screen length
tl.to('#bg-far',  { x: -30, duration: DUR, ease: 'none' }, 0);
tl.to('#bg-near', { x: -85, y: -18, duration: DUR, ease: 'none' }, 0);
window.__timelines = { main: tl };
\`\`\`

That's the whole mechanism: same direction, ~2.5–3× speed ratio between adjacent layers, \`ease: 'none'\` so the drift never visibly accelerates.

## Adapt

- Total travel: far layer 20–40px, near layer 70–110px over a ~10–15s scene. The viewer should sense it, not see it.
- A slight vertical component on the near layer only (as above) makes the drift feel dimensional rather than conveyor-belt.
- Tie the direction to the world's logic: rising worlds (dawn, growth) drift up-and-across; calm worlds drift horizontally.
- Foreground content does NOT parallax — text and hero elements stay anchored; only the world behind them moves.
- Combine with \`${SKILL_NAMES.ambientFloatLoop}\`: the drift carries the layers, individual shapes inside them float with their own periods.

## Pitfalls

- Oversize every drifting layer (\`inset: -8%\` or more) so edges never enter the canvas — a layer edge sliding into view is an instant defect.
- The full-bleed layers ARE the background — they're exempt from safe-area thinking, but anything text-bearing must not live inside a drifting layer.
- Layers are GSAP-tweened — no \`transform\` in their CSS; position them with \`inset\`/\`top\`/\`left\`.
- Keep layer contrast low (shapes near the background color). If the near layer competes with text, fade it down while body copy is on screen (\`tl.to('#bg-near', { opacity: 0.4, ... })\` during the copy's window).`,
};
